import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'utils/dayjs';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Icon from 'components/commons/Icon';
import { CURRENCY_YEAR_ARCHIVE_SOURCE } from 'constants/currencyYearArchive';
import {
  formatCurrencyYearArchiveCashRate,
  formatCurrencyYearArchiveNbuRate,
  getCurrencyYearArchiveDayByDate,
} from 'utils/currencyYearArchive';

import {
  ContentRow,
  ConverterInput,
  CurrencyCode,
  InputWrapper,
  InputsRow,
  RateArrow,
  RateRow,
  RateValue,
  ReverseButton,
  Summary,
  SummaryTitle,
  Wrapper,
} from './styles';

const TARGET_CURRENCY = 'UAH';

const parseConverterValue = value => {
  const normalized = String(value)
    .replace(',', '.')
    .replace(/[^\d.]/g, '');
  const parsed = Number.parseFloat(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
};

const CurrencyYearArchiveConverter = ({
  sourceType,
  source,
  selectedDate,
  currencyCode,
  currencyLabel,
}) => {
  const { formatMessage, formatNumber, locale } = useIntl();
  const [sourceValue, setSourceValue] = useState(1);
  const [isReversed, setIsReversed] = useState(false);
  const [isSourceFocused, setIsSourceFocused] = useState(false);

  const isNbu = sourceType === CURRENCY_YEAR_ARCHIVE_SOURCE.nbu;
  const foreignCode = currencyCode?.toUpperCase();

  const day = useMemo(
    () => getCurrencyYearArchiveDayByDate(source, selectedDate),
    [selectedDate, source]
  );

  const buyRate = day?.buy;
  const sellRate = day?.sell;
  const nbuRate = day?.rate;

  const hasRates = isNbu
    ? nbuRate != null && !Number.isNaN(Number(nbuRate))
    : buyRate != null &&
      sellRate != null &&
      !Number.isNaN(Number(buyRate)) &&
      !Number.isNaN(Number(sellRate));

  const usdToUahRate = isNbu ? nbuRate : sellRate;
  const uahToUsdRate = isNbu ? nbuRate : buyRate;

  useEffect(() => {
    setSourceValue(1);
    setIsReversed(false);
  }, [selectedDate, sourceType, foreignCode]);

  const convertedValue = useMemo(() => {
    if (!hasRates) {
      return null;
    }

    if (isReversed) {
      if (!uahToUsdRate) {
        return null;
      }

      return sourceValue / uahToUsdRate;
    }

    if (!usdToUahRate) {
      return null;
    }

    return sourceValue * usdToUahRate;
  }, [hasRates, isReversed, sourceValue, uahToUsdRate, usdToUahRate]);

  const formattedDate = useMemo(() => {
    if (!selectedDate) {
      return '';
    }

    return dayjs(selectedDate, 'YYYY-MM-DD', true)
      .locale(locale)
      .format('D MMMM YYYY');
  }, [locale, selectedDate]);

  const formatAmount = useCallback(
    value =>
      formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [formatNumber]
  );

  const formatRate = useCallback(
    value =>
      isNbu
        ? formatCurrencyYearArchiveNbuRate(value, formatNumber)
        : formatCurrencyYearArchiveCashRate(value, formatNumber),
    [formatNumber, isNbu]
  );

  const sourceDisplayValue = useMemo(() => {
    if (isSourceFocused) {
      return String(sourceValue).replace('.', ',');
    }

    return formatAmount(sourceValue);
  }, [formatAmount, isSourceFocused, sourceValue]);

  const handleSourceChange = useCallback(event => {
    setSourceValue(parseConverterValue(event.target.value));
  }, []);

  const handleReverse = useCallback(() => {
    if (convertedValue != null) {
      setSourceValue(Number(Number(convertedValue).toFixed(2)));
    }

    setIsReversed(currentValue => !currentValue);
  }, [convertedValue]);

  if (!selectedDate || !foreignCode || !hasRates) {
    return null;
  }

  const leftCurrency = isReversed ? TARGET_CURRENCY : foreignCode;
  const rightCurrency = isReversed ? foreignCode : TARGET_CURRENCY;
  const summaryRate = formatRate(usdToUahRate);

  return (
    <Wrapper>
      <ContentRow>
        <Summary>
          <SummaryTitle>
            {formatMessage(
              { id: 'currencyYearArchivePage.converter.title' },
              {
                currency: currencyLabel,
                date: formattedDate,
              }
            )}
          </SummaryTitle>
          <RateRow>
            <span>{`1 ${foreignCode}`}</span>
            <RateArrow aria-hidden>
              <Icon name="arrowForward" />
            </RateArrow>
            <RateValue>{`${summaryRate} ${TARGET_CURRENCY}`}</RateValue>
          </RateRow>
        </Summary>

        <InputsRow>
          <InputWrapper $isFocused={isSourceFocused}>
            <ConverterInput
              type="text"
              inputMode="decimal"
              value={sourceDisplayValue}
              onChange={handleSourceChange}
              onFocus={() => setIsSourceFocused(true)}
              onBlur={() => setIsSourceFocused(false)}
              aria-label={leftCurrency}
            />
            <CurrencyCode>{leftCurrency}</CurrencyCode>
          </InputWrapper>

          <ReverseButton
            type="button"
            aria-label={formatMessage({
              id: 'currencyYearArchivePage.converter.reverse',
            })}
            onClick={handleReverse}
          >
            <Icon name="arrows" />
          </ReverseButton>

          <InputWrapper>
            <ConverterInput
              type="text"
              readOnly
              value={convertedValue != null ? formatAmount(convertedValue) : ''}
              aria-label={rightCurrency}
              tabIndex={-1}
            />
            <CurrencyCode>{rightCurrency}</CurrencyCode>
          </InputWrapper>
        </InputsRow>
      </ContentRow>
    </Wrapper>
  );
};

CurrencyYearArchiveConverter.propTypes = {
  sourceType: PropTypes.oneOf(Object.values(CURRENCY_YEAR_ARCHIVE_SOURCE))
    .isRequired,
  source: PropTypes.shape({
    hasData: PropTypes.bool,
    months: PropTypes.arrayOf(PropTypes.object),
  }),
  selectedDate: PropTypes.string,
  currencyCode: PropTypes.string.isRequired,
  currencyLabel: PropTypes.string.isRequired,
};

CurrencyYearArchiveConverter.defaultProps = {
  source: null,
  selectedDate: null,
};

export default memo(CurrencyYearArchiveConverter);
